/**
 * Profanity filter for user-chosen display names (client mirror).
 *
 * Whole-token matching (precision over recall): split on non-letter boundaries,
 * normalize common leetspeak, and flag a token ONLY when it exactly matches a
 * bad word. Blocks "VAN DICK" while leaving real surnames like "Dickson" alone.
 *
 * Keep this list identical to the server source of truth in
 * `server/src/helpers/profanity.helper.js`. The server is authoritative; this
 * is a UX gate so users get instant feedback before the request is made.
 */

export const BAD_WORDS: ReadonlySet<string> = new Set([
  'fuck', 'fucker', 'fucking', 'fuk', 'motherfucker', 'fucked',
  'shit', 'bullshit', 'shitter',
  'bitch', 'bastard',
  'asshole', 'arsehole',
  'dick', 'dickhead',
  'cock', 'cunt', 'pussy', 'penis', 'vagina',
  'wanker', 'whore', 'slut', 'dildo',
  'nigger', 'nigga', 'faggot', 'retard',
  'twat', 'prick', 'bollocks',
  'boobs', 'tits', 'cum',
])

export const CENSOR_MASK = '****'

const LEET_MAP: Record<string, string> = {
  '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '8': 'b', '@': 'a', $: 's',
}

const TOKEN_REGEX = /[A-Za-z0-9@$]+/g

/** Collapse a raw token to its comparable form: lowercase, leet-substituted, letters only. */
export function normalizeToken(token: string): string {
  if (!token || typeof token !== 'string') return ''
  return token
    .toLowerCase()
    .split('')
    .map((ch) => LEET_MAP[ch] ?? ch)
    .join('')
    .replace(/[^a-z]/g, '')
}

/** Return the raw tokens in `text` whose normalized form is a bad word. */
export function findProfaneWords(text: string): string[] {
  if (!text || typeof text !== 'string') return []
  const matches = text.match(TOKEN_REGEX) ?? []
  return matches.filter((token) => BAD_WORDS.has(normalizeToken(token)))
}

/** True when `text` contains at least one bad word token. */
export function containsProfanity(text: string): boolean {
  return findProfaneWords(text).length > 0
}

/** Replace each bad word token with {@link CENSOR_MASK}, preserving surrounding text. */
export function censorProfanity(text: string): string {
  if (!text || typeof text !== 'string') return text
  return text.replace(TOKEN_REGEX, (token) => (
    BAD_WORDS.has(normalizeToken(token)) ? CENSOR_MASK : token
  ))
}
